import type { AgentContext, SessionStatus } from '../../types';
import { pluralizeString } from '../../utils/common';
import {
  AiAgent,
  Announcement,
  ChatMode,
  ChatStarter,
  ChatStarterAction,
  ModelProvider,
  Suggestion,
} from './types';

export const HISTORY_PAGE_SIZE = 50;
export const SIDE_PANEL_WIDTH = 360;

export const SESSION_ACTIVE_STATUSES: SessionStatus[] = [
  'queued',
  'running',
  'waiting_for_input',
];

export const SESSION_SETTLED_STATUSES: SessionStatus[] = [
  'idle',
  'cancelled',
  'failed',
];

export const CHAT_MODES: { key: ChatMode; label: string }[] = [
  { key: 'agent', label: 'Edit' },
  { key: 'ask', label: 'Ask' },
];

export const SHOW_ANNOUNCEMENTS_ONLY_IN_EMPTY_STATE = true;

export const ANNOUNCEMENTS: Announcement[] = [
  {
    prefix: 'New',
    message: 'Manage cloud tests in bulk.',
  },
];

// AWS Bedrock models available in the prod region (eu-central-1).
export const AI_MODELS: ModelProvider[] = [
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-opus-5-5',
    label: 'Opus 5.5',
    description: 'Best for everyday, complex tasks',
  },
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-sonnet-5-5',
    label: 'Sonnet 5.5',
    description: 'Efficient for routine tasks',
  },
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-haiku-4-5-20251001-v1:0',
    label: 'Haiku 4.5',
    description: 'Fastest for quick answers',
  },
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-opus-5',
    label: 'Opus 5',
  },
  {
    type: 'bedrock',
    modelName: 'eu.anthropic.claude-sonnet-5',
    label: 'Sonnet 5',
  },
];

const DEFAULT_MODEL_NAME = 'eu.anthropic.claude-sonnet-5';

export const buildByokModel = (
  provider: 'anthropic' | 'openai',
  model: string,
): ModelProvider => ({
  type: provider,
  modelName: model,
  label: model,
  description: 'Uses your own API key',
});

export const DEFAULT_MODEL: ModelProvider =
  AI_MODELS.find((model) => model.modelName === DEFAULT_MODEL_NAME) ??
  AI_MODELS[0];

export const AI_AGENT: AiAgent = {
  label: 'AI Agent',
  placeholder: 'Ask Elementary to build, fix, or explore',
};

export type RouteChatConfig = {
  suggestions: Suggestion[];
};

export const FALLBACK_CHAT_CONFIG: RouteChatConfig = {
  suggestions: [
    'Show me my data health overview',
    'Are there any new open incidents?',
    'Which models need better test coverage?',
  ],
};

export const ROUTE_CHAT_CONFIGS: {
  pattern: string;
  config: RouteChatConfig;
}[] = [
  {
    pattern: '/catalog',
    config: {
      suggestions: [
        'Find a data asset',
        "Find assets that haven't been updated",
        'Find assets with open incidents',
        'Suggest tests for my assets',
      ],
    },
  },
  {
    pattern: '/incidents',
    config: {
      suggestions: [
        'Investigate and group incidents by root cause',
        'Classify and assign incidents',
        'Summarize open incidents',
        'Analyze the most impactful incidents',
      ],
    },
  },
  {
    pattern: '/test-management',
    config: {
      suggestions: [
        'Edit existing tests',
        'Remove existing tests',
        'Find and fix coverage gaps',
      ],
    },
  },
  {
    pattern: '/report/model-runs',
    config: {
      suggestions: [
        'Which models are slowest?',
        'Show me models with increasing run times',
        'Compare execution times across environments',
      ],
    },
  },
  {
    pattern: '/report/dashboard',
    config: {
      suggestions: [
        'Find assets with critical issues',
        'Summarize incidents from the past week',
        'Find incidents hitting critical assets',
      ],
    },
  },
  {
    pattern: '/data-health',
    config: {
      suggestions: [
        'Break down health by tag',
        'Show health for critical assets',
        'Break down health by owner',
      ],
    },
  },
  {
    pattern: '/report/lineage',
    config: {
      suggestions: [
        'Which assets depend on this source?',
        'Are there any test failures in this path?',
      ],
    },
  },
];

const asAssetName = (label: string): string => `\`${label.replace(/`/g, '')}\``;

const describeAttached = (
  context: AgentContext | undefined,
  singular: string,
  plural: string,
): string | undefined => {
  const count = context?.assetIds?.length ?? 0;
  if (!count) return undefined;
  const labels = context?.displayLabels ?? [];
  if (count === 1 && labels.length === 1) return asAssetName(labels[0]);
  return pluralizeString(count, singular, plural);
};

const action = (label: string, prompt = label): ChatStarterAction => ({
  label,
  prompt,
});

const ADD_TESTS_WITHOUT_CONTEXT: ChatStarter = {
  message:
    "Let's add some tests. Tell me which table or column to cover, or start from one of these.",
  actions: [
    action('Find my least covered assets'),
    action('Suggest tests for my critical assets'),
    action('Add freshness and volume tests'),
  ],
};

const CHAT_STARTERS = {
  addTests: (context?: AgentContext): ChatStarter => {
    const attached = describeAttached(
      context,
      'selected asset',
      'selected assets',
    );
    if (!attached) return ADD_TESTS_WITHOUT_CONTEXT;

    return {
      message: `Let's add tests for ${attached}. I can look at the data and suggest what's worth monitoring, or you can tell me exactly what you need.`,
      actions: [
        action(
          'Suggest tests for this data',
          'Suggest tests for the selected assets.',
        ),
        action(
          'Show current test coverage',
          'Show the current test coverage for the selected assets.',
        ),
        action(
          'Add freshness and volume tests',
          'Add freshness and volume tests to the selected assets.',
        ),
        action(
          'Add column-level tests',
          'Add column-level tests to the selected assets.',
        ),
      ],
    };
  },
  addColumnTests: (context?: AgentContext): ChatStarter => {
    const attached = describeAttached(
      context,
      'selected column',
      'selected columns',
    );
    if (!attached) return ADD_TESTS_WITHOUT_CONTEXT;

    return {
      message: `Let's add tests for ${attached}. I can profile the column values and suggest what's worth checking, or you can tell me exactly what you need.`,
      actions: [
        action(
          'Suggest tests for these columns',
          'Suggest tests for the selected columns.',
        ),
        action(
          'Show current test coverage',
          'Show the current test coverage for the selected columns.',
        ),
        action(
          'Add null and uniqueness tests',
          'Add null and uniqueness tests to the selected columns.',
        ),
        action(
          'Add accepted values tests',
          'Add accepted values tests to the selected columns.',
        ),
      ],
    };
  },
  setupEnvironment: (): ChatStarter => ({
    message:
      "Let's set up your environment. Tell me which warehouse you use and I'll fill in the connection details with you. You'll type passwords and keys into the form yourself.",
    actions: [
      action(
        'Walk me through the setup',
        'Walk me through setting up this environment, one step at a time.',
      ),
      action(
        'What details do I need?',
        'Which details do I need to connect my warehouse?',
      ),
      action(
        "Check what's missing",
        'Check the form and tell me what is still missing.',
      ),
    ],
  }),
  editEnvironment: (): ChatStarter => ({
    message:
      "Let's update this environment. Tell me what you want to change and I'll update the form for you to review and save.",
    actions: [
      action(
        'Explain the current setup',
        'Explain how this environment is currently configured.',
      ),
      action(
        'Change connection details',
        'I want to change the connection details of this environment.',
      ),
      action(
        'Update asset sources',
        'I want to update the asset sources of this environment.',
      ),
    ],
  }),
};

export type ChatStarterKey = keyof typeof CHAT_STARTERS;

export const getChatStarter = (
  key: ChatStarterKey,
  context?: AgentContext,
): ChatStarter => CHAT_STARTERS[key](context);
